import React from 'react';
import { Link } from 'react-router-dom';
import CopyButton from '../Common/CopyButton';
import CopyPrompt from '../Common/CopyPrompt';

const CreateOrchestrationAgentPage = () => {
  const orchestrationAgentData = {
    name: 'Campus Tour and Academic Advising Agent for Lewis',
    description: 'Helps students navigate campus and resolve academic difficulties.',
    instructions: `You are a helpful student assistant. You have two agents available to route student queries to.

Campus Tour Agent – Navigates around campus, provides building locations, and related information.

Academic Student Advising Agent – Helps students connect to the right academic advisor.`,
    conversationStarters: [
      'Where can I find parking on campus?',
      'Where is the library located?',
      'I am struggling with course selection. Who can advise me?'
    ],
    subAgents: [
      {
        name: 'Academic Advising Agent',
        handoverName: 'AcademicAdvisor',
        description: 'Answers queries related to academic planning, course selection, and advisor connections.'
      },
      {
        name: 'Campus Tour Agent',
        handoverName: 'CampusGuide',
        description: 'Handles queries related to campus navigation, building locations, and facilities.'
      }
    ]
  };

  return (
    <div className="min-h-screen bg-purple-50 pb-20">
      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8 sm:py-12">
        {/* Header */}
        <div className="text-center mb-8 sm:mb-12">
          <h1 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            Orchestration Agent Lab
          </h1>
          <p className="text-base sm:text-lg md:text-xl text-gray-700 max-w-4xl mx-auto leading-relaxed px-2">
            Create an advanced multi-agent system by pairing two agents under one Supervisor Agent. 
            This new agent will have the combined capabilities of both, allowing it to seamlessly route 
            student queries to the right specialized agent.
          </p>

          <div className="mt-6 mb-4">
            <a 
              href="https://app.campusmind.ai/agents-market" 
              target="_blank" 
              rel="noopener noreferrer"
              className="inline-block bg-purple-700 text-white px-6 py-3 rounded-lg font-semibold hover:bg-purple-800 transition-all duration-200 shadow-lg hover:shadow-xl text-base sm:text-lg"
            >
              Try Orchestration Agent →
            </a>
          </div>
        </div>

        {/* Step 1: Create a New Orchestration Agent */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-blue-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-blue-600 to-blue-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">1</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Create a New Orchestration Agent</h2>
              <p className="text-gray-700 mb-4">Navigate to the Agent Marketplace and create a new orchestration agent.</p>
              
              <div className="bg-blue-50 border-2 border-blue-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-blue-900 mb-2">Steps:</h3>
                <ul className="text-blue-800 space-y-1">
                  <li>• From the left navigation pane, click <strong>Agent Marketplace</strong></li>
                  <li>• Click <strong>Create Agent</strong> (top-right corner)</li>
                  <li>• Select <strong>Orchestration Agent</strong> from the list of agent types</li>
                </ul>
              </div>

              {/* Screenshots */}
              <div className="space-y-4">
                <div className="flex justify-center">
                  <img
                    src="/step1-create-orchestration-agent.gif"
                    alt="Creating orchestration agent - Step 1"
                    className="w-full max-w-2xl rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="w-full max-w-2xl bg-gray-200 rounded-lg p-8 text-center border-2 border-dashed border-gray-400" style={{display: 'none'}}>
                    <p className="text-gray-600 font-medium">
                      📸 step1-create-orchestration-agent.gif
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      (Image not loading - check console for errors)
                    </p>
                  </div>
                </div>
                <div className="flex justify-center">
                  <img
                    src="/step1b-create-orchestration-agent.png"
                    alt="Creating orchestration agent - Step 1b"
                    className="w-full max-w-2xl rounded-lg shadow-md"
                    onError={(e) => {
                      e.target.style.display = 'none';
                      e.target.nextSibling.style.display = 'block';
                    }}
                  />
                  <div className="w-full max-w-2xl bg-gray-200 rounded-lg p-8 text-center border-2 border-dashed border-gray-400" style={{display: 'none'}}>
                    <p className="text-gray-600 font-medium">
                      📸 step1b-create-orchestration-agent.png
                    </p>
                    <p className="text-gray-500 text-sm mt-2">
                      (Image not loading - check console for errors)
                    </p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Step 2: Fill in Agent Details */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-green-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-green-600 to-green-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">2</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Fill in Agent Details</h2>
              <p className="text-gray-700 mb-4">Enter the following information for your orchestration agent.</p>
              
              <div className="bg-green-50 border-2 border-green-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-green-900 mb-2">Fields to fill:</h3>
                <ul className="text-green-800 space-y-1">
                  <li>• <strong>Name:</strong> Campus Tour and Academic Advising Agent for Lewis (replace <em>Lewis</em> with your own name)</li>
                  <li>• <strong>Description:</strong> Helps students navigate campus and resolve academic difficulties</li>
                  <li>• <strong>Category:</strong> Student Affairs</li>
                </ul>
              </div>

              {/* Agent Creation Form */}
              <div className="bg-gray-50 rounded-lg p-6 mb-6">
                <h3 className="text-lg font-semibold text-gray-900 mb-4">Agent Configuration</h3>
                
                {/* Name field */}
                <div className="mb-4">
                  <label className="block font-semibold mb-2">Name</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={orchestrationAgentData.name}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700"
                    />
                    <CopyButton value={orchestrationAgentData.name} />
                  </div>
                </div>

                {/* Description field */}
                <div className="mb-4">
                  <label className="block font-semibold mb-2">Description</label>
                  <div className="flex items-center gap-2">
                    <input
                      type="text"
                      readOnly
                      value={orchestrationAgentData.description}
                      className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700"
                    />
                    <CopyButton value={orchestrationAgentData.description} />
                  </div>
                </div>

                {/* Instructions field */}
                <div className="mb-4">
                  <label className="block font-semibold mb-2">Instructions</label>
                  <div className="flex items-start gap-2">
                    <textarea
                      readOnly
                      value={orchestrationAgentData.instructions}
                      className="flex-1 min-h-24 px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700 resize-vertical"
                    />
                    <CopyButton value={orchestrationAgentData.instructions} />
                  </div>
                </div>

                {/* Conversation starters */}
                <div className="mb-4">
                  <label className="block font-semibold mb-2">Conversation Starters</label>
                  <div className="space-y-2">
                    {orchestrationAgentData.conversationStarters.map((starter, index) => (
                      <div key={index} className="flex items-center gap-2">
                        <input
                          type="text"
                          readOnly
                          value={starter}
                          className="flex-1 px-4 py-3 border border-gray-300 rounded-lg bg-white text-gray-700"
                        />
                        <CopyButton value={starter} />
                      </div>
                    ))}
                  </div>
                </div>
              </div>


              {/* Screenshot for Step 2 */}
              <div className="flex justify-center mb-4">
                <img
                  src="/step2-agent-details.png"
                  alt="Agent details form - Step 2"
                  className="w-full max-w-2xl rounded-lg shadow-md"
                />
              </div>

              <div className="bg-green-100 border-2 border-green-400 rounded-lg p-4">
                <p className="text-green-800 font-semibold">
                  ✅ Your orchestration agent is now created.
                </p>
                <p className="text-green-700 mt-2">
                  Click <strong>Next</strong> once all fields are completed.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 3: Connect Sub-Agents */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-purple-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-purple-600 to-purple-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">3</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Connect Sub-Agents</h2>
              <p className="text-gray-700 mb-4">Connect the two sub-agents that will work under your orchestration agent.</p>
              
              <div className="bg-purple-50 border-2 border-purple-300 rounded-lg p-4 mb-6">
                <h3 className="font-semibold text-purple-900 mb-2">Steps:</h3>
                <ul className="text-purple-800 space-y-1">
                  <li>• In the <strong>Connected Agents</strong> screen, select:</li>
                  <li className="ml-4">- Academic Advising Agent</li>
                  <li className="ml-4">- Campus Tour Agent</li>
                  <li>• Click <strong>Next</strong> to proceed</li>
                </ul>
              </div>

              {/* Sub-Agents Configuration */}
              <div className="grid md:grid-cols-2 gap-4 mb-6">
                {orchestrationAgentData.subAgents.map((agent, index) => (
                  <div key={index} className="bg-gray-50 rounded-lg p-4 border-2 border-gray-200">
                    <h4 className="font-semibold text-gray-900 mb-2">{agent.name}</h4>
                    <div className="space-y-2">
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Handover Name:</label>
                        <div className="flex items-center gap-2">
                          <input
                            type="text"
                            readOnly
                            value={agent.handoverName}
                            className="flex-1 px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 text-sm"
                          />
                          <CopyButton value={agent.handoverName} />
                        </div>
                      </div>
                      <div>
                        <label className="block text-sm font-medium text-gray-700">Description:</label>
                        <div className="flex items-start gap-2">
                          <textarea
                            readOnly
                            value={agent.description}
                            className="flex-1 min-h-16 px-3 py-2 border border-gray-300 rounded bg-white text-gray-700 text-sm resize-vertical"
                          />
                          <CopyButton value={agent.description} />
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>


              {/* Screenshots for Step 3 */}
              <div className="space-y-4 mb-4">
                <div className="flex justify-center">
                  <img
                    src="/step3-connect-subagents.png"
                    alt="Connected agents screen - Step 3"
                    className="w-full max-w-2xl rounded-lg shadow-md"
                  />
                </div>
                <div className="flex justify-center">
                  <img
                    src="/step3b-connect-subagents.png"
                    alt="Handover configuration - Step 3b"
                    className="w-full max-w-2xl rounded-lg shadow-md"
                  />
                </div>
              </div>

              <div className="bg-purple-100 border-2 border-purple-400 rounded-lg p-4">
                <p className="text-purple-800 font-semibold">
                  Click <strong>Next</strong> to continue.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 4: Publish Your Orchestration Agent */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-orange-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-orange-600 to-orange-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">4</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Publish Your Orchestration Agent</h2>
              <p className="text-gray-700 mb-4">Publish your orchestration agent to make it accessible.</p>
              
              <div className="bg-orange-50 border-2 border-orange-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-orange-900 mb-2">Steps:</h3>
                <ul className="text-orange-800 space-y-1">
                  <li>• Enable the <strong>Publish to Marketplace</strong> toggle</li>
                  <li>• Click <strong>Update Agent</strong> to confirm</li>
                </ul>
              </div>

              {/* Screenshot for Step 4 */}
              <div className="flex justify-center">
                <img
                  src="/step4-publish-agent.png"
                  alt="Publish to marketplace toggle - Step 4"
                  className="w-full max-w-2xl rounded-lg shadow-md"
                />
              </div>
            </div>
          </div>
        </div>

        {/* Step 5: Manage User Access */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-indigo-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-indigo-600 to-indigo-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">5</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Manage User Access</h2>
              <p className="text-gray-700 mb-4">Control who can view, chat with, or edit your agent.</p>
              
              <div className="bg-indigo-50 border-2 border-indigo-300 rounded-lg p-4">
                <h3 className="font-semibold text-indigo-900 mb-2">Tabs available:</h3>
                <ul className="text-indigo-800 space-y-1">
                  <li>• Manage visibility and chat permissions</li>
                  <li>• Manage editor and manager access</li>
                </ul>
                <p className="text-indigo-700 mt-2">
                  For this exercise, skip this step and click <strong>Finish</strong> to complete the orchestration setup.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Step 6: Test Your Orchestration Agent */}
        <div className="bg-white rounded-xl shadow-lg p-6 md:p-8 mb-8 border-l-8 border-teal-500">
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <div className="w-12 h-12 bg-gradient-to-br from-teal-600 to-teal-700 rounded-full flex items-center justify-center text-white shadow-lg">
                <span className="text-2xl font-bold">6</span>
              </div>
            </div>
            <div className="flex-1">
              <h2 className="text-2xl font-bold text-gray-900 mb-4">Test Your Orchestration Agent</h2>
              <p className="text-gray-700 mb-4">Now that your orchestration agent is built, let's test it.</p>
              
              <div className="bg-teal-50 border-2 border-teal-300 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-teal-900 mb-2">Steps:</h3>
                <ul className="text-teal-800 space-y-1">
                  <li>• Go to the <strong>Marketplace</strong></li>
                  <li>• Scroll to the <strong>Student Affairs</strong> section</li>
                  <li>• Locate your <em>Campus Tour and Academic Advising Agent</em></li>
                  <li>• Click to open the chat window</li>
                </ul>
              </div>

              <div className="bg-gray-50 rounded-lg p-4 mb-4">
                <h3 className="font-semibold text-gray-900 mb-2">Try asking:</h3>
                <div className="space-y-2">
                  {orchestrationAgentData.conversationStarters.map((question, index) => (
                    <div key={index} className="flex items-center justify-between gap-3 p-3 bg-white rounded-lg border border-gray-200">
                      <span className="text-gray-700 flex-1">"{question}"</span>
                      <CopyPrompt text={question} />
                    </div>
                  ))}
                </div>
              </div>


              {/* Screenshots for Step 6 */}
              <div className="space-y-4 mb-4">
                <div className="flex justify-center">
                  <img
                    src="/step6-test-orchestration.png"
                    alt="Marketplace with orchestration agent - Step 6"
                    className="w-full max-w-2xl rounded-lg shadow-md"
                  />
                </div>
                <div className="flex justify-center">
                  <img
                    src="/step6b-test-orchestration.png"
                    alt="Chat interface with routing - Step 6b"
                    className="w-full max-w-2xl rounded-lg shadow-md"
                  />
                </div>
              </div>

              <div className="bg-teal-100 border-2 border-teal-400 rounded-lg p-4">
                <p className="text-teal-800 font-semibold">
                  Observe how your orchestration agent automatically routes queries to the right sub-agent.
                </p>
              </div>
            </div>
          </div>
        </div>

        {/* Wrap-Up */}
        <div className="bg-gradient-to-r from-purple-600 to-purple-800 rounded-xl shadow-xl p-8 text-white mb-8">
          <h2 className="text-2xl font-bold mb-4 text-center">Wrap-Up</h2>
          <p className="text-center text-purple-100 mb-4 text-lg leading-relaxed">
            You've successfully built an Orchestration Agent — a powerful multi-agent system that combines 
            the strengths of multiple specialized agents under one unified assistant.
          </p>
          <p className="text-center text-purple-100 text-lg leading-relaxed">
            This forms the foundation for building smarter, scalable AI ecosystems that can collaborate dynamically.
          </p>
          <div className="text-center mt-6">
            <span className="text-3xl">🎉</span>
            <span className="text-2xl font-bold ml-2">Great work!</span>
            <span className="text-3xl ml-2">🚀</span>
          </div>
        </div>

        {/* Navigation */}
        <div className="flex justify-center gap-4 md:gap-8 flex-wrap">
          <Link
            to="/create-agent"
            className="bg-gray-600 text-white px-6 py-3 rounded-lg font-medium hover:bg-gray-700 transition-colors duration-200"
          >
            Back: Create AI Agent
          </Link>
          <Link
            to="/accessibility-agent"
            className="bg-purple-700 text-white px-6 py-3 rounded-lg font-medium hover:bg-purple-800 transition-colors duration-200"
          >
            Next: Accessibility Agent
          </Link>
        </div>
      </div>
    </div>
  );
};

export default CreateOrchestrationAgentPage;
